/**
 * Pure edits of the department-tile draft on `/settings/departments`. The page saves the whole
 * list (`PUT /v1/settings/departments`); array order is the sort order.
 */
import type { DepartmentSettings, SageDepartment, TileInput } from '@/api/types'
import { type AutoGroup, planHierarchy } from '@/lib/sageHierarchy'

export interface TileDraft {
  code: string
  name: string
  source_depts: string[]
  note: string | null
  hidden: boolean
}

export function draftFrom(settings: DepartmentSettings): TileDraft[] {
  return [...settings.tiles]
    .sort((a, b) => a.sort - b.sort)
    .map((t) => ({ code: t.code, name: t.name, source_depts: [...t.source_depts], note: t.note, hidden: t.hidden }))
}

export function toInput(draft: TileDraft[]): TileInput[] {
  return draft.map((t) => ({
    code: t.code,
    name: t.name.trim(),
    source_depts: t.source_depts,
    note: t.note,
    hidden: t.hidden,
  }))
}

export function isDirty(draft: TileDraft[], settings: DepartmentSettings): boolean {
  return JSON.stringify(draft) !== JSON.stringify(draftFrom(settings))
}

/** Sage departments no tile in the draft lists (ignores the hierarchy; see `sageGroups`). */
export function ungrouped(draft: TileDraft[], departments: SageDepartment[]): SageDepartment[] {
  const grouped = new Set(draft.flatMap((t) => t.source_depts))
  return departments.filter((d) => !grouped.has(d.id))
}

/** An automatic tile the draft leaves to Sage: a top-level department and its sub-departments. */
export interface SageGroup extends AutoGroup {
  dept: SageDepartment
  /** Sub-departments (members after the root). */
  children: SageDepartment[]
}

/**
 * What the draft leaves to Sage's hierarchy (the runway's automatic tiles), and the Sage
 * sub-departments each configured tile picks up through a listed parent. Recomputed on every edit,
 * so the page shows the effect before saving.
 */
export function sageGroups(
  draft: TileDraft[],
  departments: SageDepartment[],
): { groups: SageGroup[]; inherited: Map<string, string[]> } {
  const plan = planHierarchy(draft, departments)
  const byId = new Map(departments.map((d) => [d.id, d]))
  const fallback = (id: string): SageDepartment => ({
    id,
    title: id,
    status: null,
    tile: null,
    parent_id: null,
    counted_in: id,
    via: 'sage',
  })
  return {
    inherited: plan.inherited,
    groups: plan.groups.map((g) => ({
      ...g,
      dept: byId.get(g.root) ?? fallback(g.root),
      children: g.members.slice(1).map((id) => byId.get(id) ?? fallback(id)),
    })),
  }
}

/** A short summary of what saving the draft changes, for the confirmation dialog. */
export function draftChanges(draft: TileDraft[], settings: DepartmentSettings): string[] {
  const before = draftFrom(settings)
  const old = new Map(before.map((t) => [t.code, t]))
  const next = new Map(draft.map((t) => [t.code, t]))
  const out: string[] = []
  for (const t of draft) {
    const prev = old.get(t.code)
    if (!prev) out.push(`New tile ${t.code} (${t.source_depts.join(', ')})`)
    else {
      if (prev.name !== t.name) out.push(`${t.code} renamed to "${t.name.trim()}"`)
      if (prev.hidden !== t.hidden) out.push(`${t.code} ${t.hidden ? 'hidden from' : 'shown on'} the runway`)
      if (prev.source_depts.join() !== t.source_depts.join())
        out.push(`${t.code} now groups ${t.source_depts.join(', ')}`)
    }
  }
  for (const t of before) {
    if (!next.has(t.code)) out.push(`Tile ${t.code} removed: its departments follow Sage again`)
  }
  const order = (tiles: TileDraft[]) =>
    tiles
      .map((t) => t.code)
      .filter((c) => old.has(c) && next.has(c))
      .join()
  if (order(before) !== order(draft)) out.push('Tile order changed')
  return out
}

const CODE = /[^A-Za-z0-9_.-]/g

function freeCode(draft: TileDraft[], wanted: string): string {
  const base = wanted.replace(CODE, '').slice(0, 28) || 'TILE'
  const taken = new Set(draft.map((t) => t.code.toUpperCase()))
  if (!taken.has(base.toUpperCase())) return base
  for (let i = 2; ; i += 1) {
    const code = `${base}-${i}`
    if (!taken.has(code.toUpperCase())) return code
  }
}

export function addTile(draft: TileDraft[], dept: SageDepartment): TileDraft[] {
  return [
    ...draft,
    { code: freeCode(draft, dept.id), name: dept.title, source_depts: [dept.id], note: null, hidden: false },
  ]
}

/** Move a department into `code`'s tile (out of any other tile). */
export function mergeInto(draft: TileDraft[], code: string, deptId: string): TileDraft[] {
  return draft.map((t) => {
    const without = t.source_depts.filter((d) => d !== deptId)
    return t.code === code ? { ...t, source_depts: [...without, deptId] } : { ...t, source_depts: without }
  })
}

/** Take a department out of a tile (the last one cannot leave: remove the tile instead). */
export function removeDept(draft: TileDraft[], code: string, deptId: string): TileDraft[] {
  return draft.map((t) =>
    t.code === code && t.source_depts.length > 1
      ? { ...t, source_depts: t.source_depts.filter((d) => d !== deptId) }
      : t,
  )
}

export function removeTile(draft: TileDraft[], code: string): TileDraft[] {
  return draft.filter((t) => t.code !== code)
}

export function moveTile(draft: TileDraft[], index: number, delta: -1 | 1): TileDraft[] {
  const target = index + delta
  if (target < 0 || target >= draft.length) return draft
  const next = [...draft]
  const [moved] = next.splice(index, 1)
  if (moved) next.splice(target, 0, moved)
  return next
}

export function renameTile(draft: TileDraft[], code: string, name: string): TileDraft[] {
  return draft.map((t) => (t.code === code ? { ...t, name } : t))
}

export function toggleHidden(draft: TileDraft[], code: string): TileDraft[] {
  return draft.map((t) => (t.code === code ? { ...t, hidden: !t.hidden } : t))
}

/** A message for a draft the API would reject, or null. */
export function draftError(draft: TileDraft[]): string | null {
  const blank = draft.find((t) => !t.name.trim())
  if (blank) return `Tile ${blank.code} needs a name.`
  const long = draft.find((t) => t.name.trim().length > 80)
  if (long) return `Tile ${long.code}: the name can be at most 80 characters.`
  return null
}
